import {
    createBrowserRouter,
    RouterProvider,
    useLocation,
} from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ReactNode, useLayoutEffect, Suspense } from "react";
import App from "../App";
import { 
    LazyHome as Home, 
    LazyContactUs as ContactUs, 
    LazyFeatures as Features, 
    LazyPricing as Pricing, 
    LazyUserAgreement as UserAgreement, 
    LazyPrivacyPolicy as PrivacyPolicy 
} from "../utils/lazyComponents";

// Loading fallback component
const LoadingSpinner = () => (
    <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
);

// Page transition settings
const pageTransition = {
    initial: { opacity: 0, x: -100 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 100 },
    transition: { duration: 0.5 },
};

type MotionWrapperProps = {
    children: ReactNode;
};

// Layout for the pages with transition
const MotionWrapper = ({ children }: MotionWrapperProps) => {
    const location = useLocation();

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={location.pathname}
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageTransition}
                transition={pageTransition.transition}>
                {children}
            </motion.div>
        </AnimatePresence>
    );
};

export const AppRoutes = createBrowserRouter([
    {
        path: "/",
        element: <App />,
        children: [
            {
                path: "/",
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <MotionWrapper>
                            <Home />
                        </MotionWrapper>
                    </Suspense>
                ),
            },
            {
                path: "/contact-us",
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <MotionWrapper>
                            <ContactUs />
                        </MotionWrapper>
                    </Suspense>
                ),
            },
            // {
            //     path: "/about-us",
            //     element: (
            //         <MotionWrapper>
            //             <AboutUs />
            //         </MotionWrapper>
            //     ),
            // },
            {
                path: "/features",
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <MotionWrapper>
                            <Features />
                        </MotionWrapper>
                    </Suspense>
                ),
            },
            {
                path: "/pricing",
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <MotionWrapper>
                            <Pricing />
                        </MotionWrapper>
                    </Suspense>
                ),
            },
            {
                path: "/privacy-policy",
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <MotionWrapper>
                            <PrivacyPolicy />
                        </MotionWrapper>
                    </Suspense>
                ),
            },
            {
                path: "/user-agreement",
                element: (
                    <Suspense fallback={<LoadingSpinner />}>
                        <MotionWrapper>
                            <UserAgreement />
                        </MotionWrapper>
                    </Suspense>
                ),
            },
        ],
    },
]);

const AppWrapper = () => {
    return <RouterProvider router={AppRoutes} />;
};

export default AppWrapper;
